"use client";

import { mediaUrl } from "@/lib/media";
import { PROJECTS } from "@/assets/static/data";
import ArrowBack from "@/assets/vectors/ArrowBack";
import ArrowForward from "@/assets/vectors/ArrowForward";
import ChevronRight from "@/assets/vectors/ChevronRight";
import Button from "@/components/button/Button";
import CaseStudy from "@/types/CaseStudy";
import dictionary from "@/utils/dictionary";
import clsx from "clsx";
import Image, { type ImageProps } from "next/image";
import Link from "next/link";
import { useState } from "react";
import styles from "./case_study_hero.module.scss";

interface CaseStudyHeroProps {
    caseStudy: CaseStudy;
    viewsElement?: React.ReactNode;
}

export default function CaseStudyHero({ caseStudy, viewsElement }: CaseStudyHeroProps) {
    const project = PROJECTS.find(it => it.name == caseStudy.project_name)!
    const [heroImage, setHeroImage] = useState<ImageProps["src"]>(mediaUrl(caseStudy.hero_image))
    return (
        <section
            className={styles.container}
            data-project={caseStudy.slug}
        >
            <div className={styles.topper}>
                <Link
                    href="/#section_projects"
                    className={styles.link}
                >
                    <ArrowBack className={styles.icon} data-scroll-animation="come-from-top" />
                    <span
                        data-scroll-animation="come-from-top"
                        data-scroll-animation-delay="250"
                    >{dictionary.global.back_to_projects}</span>
                </Link>

                <Link
                    href="#section_share"
                    className={clsx(styles.link, styles.share)}
                >
                    <span
                        data-scroll-animation="come-from-top"
                        data-scroll-animation-delay="250"
                    >{dictionary.global.share_case_study}</span>
                    <ArrowForward className={clsx(styles.icon, styles.forward)} data-scroll-animation="come-from-top" width={15} />
                </Link>
            </div>

            <div className={styles.content}>
                <div className={styles.text_content}>
                    {caseStudy.hero_eyebrow && (
                        <p className={styles.eyebrow}>{caseStudy.hero_eyebrow}</p>
                    )}
                    <h1 className={styles.title} data-scroll-animation="come-from-bottom">
                        {caseStudy.hero_title}
                    </h1>
                    <p className={styles.subtitle} data-scroll-animation="come-from-bottom" data-scroll-animation-delay="250">
                        {caseStudy.hero_subtitle}
                    </p>

                    {viewsElement}
                </div>

                <div className={styles.hero_image_wrapper} data-scroll-animation-delay="500">
                    <Image
                        src={heroImage}
                        alt={caseStudy.hero_image_alt ?? caseStudy.hero_title}
                        className={styles.hero_image}
                        width={1920}
                        height={1080}
                        sizes="(max-width: 720px) 100vw, 1200px"
                        quality={85}
                        onError={() => setHeroImage(project.logo)}
                        data-scroll-animation="clear-in"
                        priority
                    />

                    {project.url ? <Button
                        label={
                            project.accessibility === "private"
                                ? dictionary.global.private_project
                                : (project.url
                                    ? (project.accessibility == "open_source" ? dictionary.global.view_repository : dictionary.global.experience_it_live)
                                    : dictionary.global.coming_soon_release)
                        }
                        href={project.accessibility === "private" ? undefined : project.url}
                        status={(project.url == undefined || project.accessibility === "private") ? "disabled" : "enabled"}
                        hrefTarget="_blank"
                        size="large"
                        endIcon={(project.url == undefined || project.accessibility === "private") ? undefined : ChevronRight}
                        iconSize={10}
                    /> : undefined}

                </div>
            </div>
        </section>
    );
}
